import {
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from "react";
import { strFromU8, strToU8, unzlibSync, zlibSync } from "fflate";
import { isEqual } from "lodash";
import qs, { IParseOptions } from "qs";
import { z, ZodType } from "zod";

import { PaginationOptions } from "../generated/app-service-gql/graphql";

type Encoder = "qs" | "compress";

export const qsSettings: IParseOptions = {
  ignoreQueryPrefix: true,
  arrayLimit: 1000,
  parameterLimit: 1000,
  // Enabling throwing since throwing here makes weird errors more obvious
  // e.g. turning an array into an object with numbers as keys
  throwOnLimitExceeded: true,
};

const events = ["popstate"];
function subscribe(callback: () => void) {
  for (const event of events) {
    window.addEventListener(event, callback);
  }
  return () => {
    for (const event of events) {
      window.removeEventListener(event, callback);
    }
  };
}

// Can decode online with `https://gchq.github.io/CyberChef/#recipe=From_Base64('A-Za-z0-9-_',true,false)Zlib_Inflate(0,0,'Adaptive',false,false)JSON_Beautify('%20%20%20%20',false,false)`
export function decodeCompressed(input: unknown): unknown {
  if (typeof input !== "string") {
    return input;
  }
  if (!/^[A-Za-z0-9_-]*={0,2}$/.test(input)) {
    return input; // Not a base64 encoded string, so cannot be compress
  }

  try {
    const padding = input.length % 4;
    const encoded = input
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(input.length + ((4 - padding) % 4), "="); // Add padding to make it a valid base64 string
    const compressed = Buffer.from(encoded, "base64");
    const buffer = unzlibSync(compressed);
    const value = JSON.parse(strFromU8(buffer));
    return value;
  } catch (error) {
    console.warn("Failed to decode compressed data:", error);
    // If there is an error, it could be because the input was not compressed but was recognised as so or it's just wrong
    // Either way, just return it as the next step will validate the shape of the data and if that fails, it will return the default value
    return input;
  }
}

export function encodeCompressed<T>(input: T): string | T | undefined {
  if (input === undefined) {
    return undefined; // If the input is undefined, we return undefined to avoid adding it to the URL
  }

  try {
    const buffer = strToU8(JSON.stringify(input));
    // zlib is not the best compression algorithm for size from the testing I've done, that was brotli
    // but this package was 8kb and gave a 43.8% reduction compared to brotli's ~1mb and 55.8% reduction (using single stringified JSON from real data)
    // However, brotli also required using WASM, meaning async was needed in this file or an hack & some special code for jest.
    const compressed = zlibSync(buffer);
    const encoded = Buffer.from(compressed).toString("base64");
    const urlSafe = encoded.replace(/=/g, "").replace(/\+/g, "-").replace(/\//g, "_"); // Make it URL safe. The padding is optional (=) so it is safe to remove.
    return urlSafe;
  } catch (error) {
    console.error("Failed to compress data:", error);
    return input; // If compression fails, return the original input
  }
}

function getParams<T>(key: string, parseValue: (value: unknown) => T, encoder: Encoder): T {
  const values = qs.parse(window.location.search, qsSettings);
  let value: unknown = values[key];

  if (encoder === "compress") {
    value = decodeCompressed(value);
  }
  const parsed = parseValue(value);
  return parsed;
}

function setParams<T>(key: string, value: T, encoder: Encoder, replaceState?: boolean) {
  const values = qs.parse(window.location.search, qsSettings);
  let encodedValue: T | string | undefined = value;

  if (encoder === "compress") {
    encodedValue = encodeCompressed(value);
  }

  const newQsValue = qs.stringify(
    { ...values, [key]: encodedValue },
    { arrayFormat: "brackets", encodeValuesOnly: true, addQueryPrefix: true },
  );
  if (newQsValue === window.location.search) {
    return; // No change, so do nothing
  }

  if (replaceState) {
    window.history.replaceState({}, "", `${window.location.pathname}${newQsValue}`);
  } else {
    window.history.pushState({}, "", `${window.location.pathname}${newQsValue}`);
  }
  // manually trigger a popstate event to update the state in useSyncExternalStore
  dispatchEvent(new PopStateEvent("popstate", { state: {} }));
}

function safeishStringify(value: unknown): string | undefined | null {
  if (value === null || value === undefined) {
    return value;
  }
  return JSON.stringify(value);
}

/**
 * This hook allows you to manage a piece of state that is synchronized with a query parameter in the URL.
 *
 * For net-new code, prefer using Zod schema + `encoder: "compress"` for the best experience (if not an object, qs encoder is fine just beware of the footguns below).
 *
 * Footguns:
 * - If encoder is `compress`, values are kept exactly, meaning `z.stringbool()` will error since it only accepts a string as input (instead of string or boolean)
 * - If encoder is `qs` (default), everything is coerced into a string with all that entails (e.g. use `z.stringbool()` to convert strings to booleans)
 * - If encoder is `qs` (default), it's very easy to accidentally create a URL which is too long and causes production issues (limit is 8,192 bytes for the **whole** URL in prod, which is lower than dev).
 *
 * @param key Which query param in the URL to use
 * @param defaultValue Value to use if the query param is not set or cannot be parsed
 * @param parser Zod schema or a function which takes a value (and the default schema) and returns the parsed value. Zod schema is preferred
 * @param options Configuration options
 * @returns [currentValue, setValue] - A tuple of the current value and a function to update the value
 */
// Overload for Zod schema (type inferred from schema)
export function useURLState<T extends ZodType<unknown>>(
  key: string,
  defaultValue: z.output<T>,
  parser: T,
  options?: {
    encoder?: "qs" | "compress";
    replaceState?: boolean;
  },
): [z.output<T>, (newValue: SetStateAction<z.output<T>>) => void];

// Overload for parseValue function (type inferred from defaultValue)
/* @deprecated Prefer using Zod schemas for URL parsing instead */
export function useURLState<T>(
  key: string,
  defaultValue: T,
  parser: (value: unknown, defaultValue: T) => T,
  options?: {
    encoder?: "qs" | "compress";
    replaceState?: boolean;
  },
): [T, (newValue: SetStateAction<T>) => void];
export function useURLState<T>(
  key: string,
  defaultValue: T,
  parser: ZodType<unknown> | ((value: unknown, defaultValue: T) => T),
  options?: {
    encoder?: "qs" | "compress";
    replaceState?: boolean;
  },
): [T, (newValue: SetStateAction<T>) => void] {
  const encoder = options?.encoder || "qs";
  const replaceState = options?.replaceState || false;

  // Stabilize defaultValue to prevent unnecessary recreations of parseValue & value
  const defaultValueRef = useRef(defaultValue);
  if (!isEqual(defaultValueRef.current, defaultValue)) {
    defaultValueRef.current = defaultValue;
  }

  const parseValue = useMemo(
    () => (value: unknown) => {
      if (typeof parser === "function") {
        return parser(value, defaultValueRef.current);
      }

      // Below occurs if parser is a Zod schema
      if (value === undefined) {
        return defaultValueRef.current;
      }
      const result = parser.safeParse(value);
      if (result.success) {
        return result.data as T;
      } else {
        console.warn(
          `Failed to parse value for query param, reverting to defaults\n`,
          result.error.issues,
        );
        return defaultValueRef.current;
      }
    },
    [parser],
  );

  const queryValue = useSyncExternalStore(
    subscribe,
    () => safeishStringify(getParams(key, parseValue, encoder)),
    () => safeishStringify(getParams(key, parseValue, encoder)),
  );

  const setURLState = useCallback(
    (newValue: SetStateAction<T>) => {
      const previousValue = getParams(key, parseValue, encoder);
      if (typeof newValue === "function") {
        newValue = (newValue as (prevState: T) => T)(previousValue);
      }
      // only update if the value has changed
      if (!isEqual(newValue, previousValue)) {
        setParams(key, newValue, encoder, replaceState);
      }
    },
    [key, parseValue, encoder, replaceState],
  );

  // memoize the value for referential equality
  const value = useMemo<T>(() => {
    // Not doing JSON.parse here because it strips undefined values and some custom
    // parser functions require undefined values to be passed through
    return queryValue ? getParams(key, parseValue, encoder) : defaultValueRef.current;
  }, [queryValue, key, parseValue, encoder]);

  return [value, setURLState];
}

/** @deprecated Prefer using `z.coerce.number()` for URL parsing instead */
export function parseNumberValue(value: unknown) {
  if (typeof value === "number") {
    return value;
  }
  if (typeof value === "string" && value && !Number.isNaN(Number(value))) {
    return Number(value);
  }
  return undefined;
}

/** @deprecated Prefer using `z.union([z.boolean(), z.stringbool()])` for URL parsing instead */
export function parseBooleanValue(value: unknown) {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "string" && value) {
    return value === "true";
  }
  return undefined;
}

export function parsePaginationUrlState(
  state: unknown,
  defaultPagination: PaginationOptions,
): PaginationOptions {
  if (!state || typeof state !== "object") {
    return defaultPagination;
  }

  const parsedState = state as PaginationOptions & { current?: unknown };

  const pageRaw =
    parsedState.page !== undefined && parsedState.page !== null
      ? parsedState.page
      : parsedState.current;
  const pageSizeRaw = parsedState.pageSize;

  const pageProvided =
    pageRaw !== undefined && pageRaw !== null && !(typeof pageRaw === "string" && pageRaw === "");
  const pageSizeProvided =
    pageSizeRaw !== undefined &&
    pageSizeRaw !== null &&
    !(typeof pageSizeRaw === "string" && pageSizeRaw === "");

  const pageResult = pageProvided ? z.coerce.number().safeParse(pageRaw) : null;
  const pageSizeResult = pageSizeProvided ? z.coerce.number().safeParse(pageSizeRaw) : null;

  if (pageResult && !pageResult.success) {
    return defaultPagination;
  }
  if (pageSizeResult && !pageSizeResult.success) {
    return defaultPagination;
  }

  return {
    page: pageResult?.success ? pageResult.data : defaultPagination.page,
    pageSize: pageSizeResult?.success ? pageSizeResult.data : defaultPagination.pageSize,
  };
}

/**
 * Sends a table back to page 1 when its page is past the last one, e.g. a page kept in the
 * URL carried over to a shorter list. Waits for `total`, so it never fires mid-load.
 */
export function useResetPageWhenPastEnd(
  currentPage: number,
  pageSize: number,
  total: number | undefined,
  setCurrentPage: (page: number) => void,
) {
  useEffect(() => {
    if (total === undefined || currentPage === 1) return;
    if ((currentPage - 1) * pageSize >= total) setCurrentPage(1);
  }, [currentPage, pageSize, total, setCurrentPage]);
}
